/* ============================================================================
   REGISTRO SGS — inmutable, trazable y verificable

   Corrige los cinco hallazgos del análisis (docs/ANALISIS-SGS.md). La forma
   sale de lo que exigen el Anexo 19 de OACI, el Doc 9859 y BARS, no de lo que
   era cómodo de programar.

   ══════════════════════════════════════════════════════════════════════════
   LAS CINCO REGLAS

   1. **Nada se borra. Nada se edita.** Un registro se CIERRA con un motivo.
      El texto original nunca cambia; lo que se agrega va como anotación
      fechada. Un sistema de reportes donde los reportes desaparecen no es un
      sistema de reportes: la confianza del que reporta se basa en que lo que
      escribió queda.

   2. **Cada cambio deja rastro.** Quién, cuándo, de qué estado a cuál y por
      qué. Es el corazón de la evidencia que pide un auditor.

   3. **La identidad se puede separar del contenido.** El Anexo 19 distingue el
      reporte confidencial del identificado. Sin esa vía, los reportes que más
      importan —los que involucran un error propio— no se escriben.

   4. **Cada registro lleva su huella.** Un número derivado de su contenido
      inmutable. Si el archivo exportado se altera o se pierde una entrada, se
      detecta. Sin esto, un registro «completo» no se puede distinguir de uno
      al que le falta algo.

   5. **Nada se da por guardado hasta que está guardado.** Esta regla NO vive
      acá sino en quien llama: este módulo devuelve el registro nuevo, y el que
      persiste tiene que confirmar la escritura ANTES de mostrarlo. Se
      documenta acá porque es la mitad del hallazgo 1.
   ══════════════════════════════════════════════════════════════════════════
   ========================================================================== */

import { validarBarreras, orientacionDeBarreras } from "./sistemaIntegrado.js";

const num = (v) => (Number.isFinite(v) ? v : null);
const texto = (v) => (typeof v === "string" ? v.trim() : "");

/* --------------------------------------------------------------- el canal */

/**
 * Los estados por los que pasa un registro, y a cuáles puede ir cada uno.
 *
 * Es el «canal de seguimiento»: un reporte que entra tiene que poder recorrer
 * un camino conocido hasta cerrarse, y cada paso queda registrado. Sin estados
 * definidos, «en trámite» significa lo que cada uno quiera y el auditor no
 * puede medir nada.
 */
export const ESTADOS = {
  nuevo: { etiqueta: "Nuevo", siguientes: ["en-analisis", "cerrado-sin-accion"] },
  "en-analisis": { etiqueta: "En análisis", siguientes: ["con-accion", "cerrado-sin-accion"] },
  "con-accion": { etiqueta: "Con acción correctiva", siguientes: ["verificando", "cerrado-sin-accion"] },
  /* La verificación de eficacia es lo que exige el tercer pilar: no alcanza
     con hacer algo, hay que comprobar que sirvió. */
  verificando: { etiqueta: "Verificando eficacia", siguientes: ["cerrado-eficaz", "con-accion"] },
  "cerrado-eficaz": { etiqueta: "Cerrado — acción eficaz", siguientes: [], final: true },
  "cerrado-sin-accion": { etiqueta: "Cerrado — sin acción", siguientes: [], final: true },
};

export const ESTADO_INICIAL = "nuevo";

/** Tipos de registro que maneja el SGS. */
export const TIPOS = ["reporte", "evento", "auditoria", "no-conformidad"];

/** ¿Se puede pasar de un estado a otro? */
export function transicionValida(desde, hacia) {
  const e = ESTADOS[desde];
  if (!e) return false;
  return e.siguientes.includes(hacia);
}

/** ¿Está cerrado? */
export function estaCerrado(estado) {
  return ESTADOS[estado]?.final === true;
}

/* ------------------------------------------------------------- la huella */

/**
 * Huella del contenido inmutable de un registro.
 *
 * No es criptografía: es una suma de comprobación que detecta alteración
 * accidental y pérdida de entradas. Para un SGS de operador alcanza — el
 * objetivo es que un archivo modificado o incompleto se note, no resistir a un
 * atacante.
 *
 * Se calcula SOLO sobre lo inmutable: si incluyera el estado o las
 * anotaciones, cambiaría en cada paso del canal y no serviría para nada.
 */
export function huellaDe(registro) {
  const base = [
    registro?.id ?? "",
    registro?.tipo ?? "",
    registro?.creado ?? "",
    registro?.descripcion ?? "",
    registro?.confidencial ? "C" : "I",
    registro?.reportante ?? "",
    /* El riesgo INICIAL entra en la huella porque es inmutable: es lo que se
       evaluó ese día. El residual NO, porque cambia al mitigar. */
    registro?.riesgoInicial
      ? `${registro.riesgoInicial.probabilidad}${registro.riesgoInicial.severidad}`
      : "",
  ].join("\u001f");

  /* FNV-1a de 32 bits: corto, estable entre plataformas y sin dependencias.
     Se devuelve en hexadecimal de ocho dígitos. */
  let h = 0x811c9dc5;
  for (let i = 0; i < base.length; i += 1) {
    h ^= base.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, "0");
}

/** ¿El registro coincide con su huella? */
export function huellaIntacta(registro) {
  if (!registro?.huella) return false;
  return huellaDe(registro) === registro.huella;
}

/* ---------------------------------------------------------------- crear */

/**
 * Crea un registro nuevo.
 *
 * @param {object} datos
 * @param {string} datos.tipo         uno de TIPOS
 * @param {string} datos.descripcion  el texto del reporte. NUNCA se edita.
 * @param {string} [datos.reportante] quién reporta
 * @param {boolean} [datos.confidencial] si la identidad va separada
 * @returns {{registro: object|null, errores: string[]}}
 */
export function crearRegistro(datos = {}, { ahora = new Date(), id = null } = {}) {
  const errores = [];

  const tipo = texto(datos?.tipo);
  if (!TIPOS.includes(tipo)) errores.push(`Tipo inválido. Debe ser uno de: ${TIPOS.join(", ")}.`);

  const descripcion = texto(datos?.descripcion);
  if (descripcion.length < 10) {
    /* Un reporte de tres palabras no se puede analizar ni auditar. El mínimo
       no es burocracia: es lo que hace falta para que alguien más entienda qué
       pasó. */
    errores.push("La descripción tiene que decir qué pasó (al menos 10 caracteres).");
  }

  const confidencial = datos?.confidencial === true;
  const reportante = texto(datos?.reportante);

  /* En un reporte identificado hace falta saber quién fue, para poder
     preguntarle. En uno confidencial NO se pide: ese es el punto. */
  if (!confidencial && !reportante) {
    errores.push("Un reporte identificado necesita quién lo reporta. "
      + "Si prefieres no dar tu nombre, marcalo como confidencial.");
  }

  if (errores.length > 0) return { registro: null, errores };

  const creado = (ahora instanceof Date ? ahora : new Date()).toISOString();

  /* La evaluación de riesgo forma parte INMUTABLE del registro.

     Antes se calculaba en el formulario, se mostraba en pantalla y se perdía.
     Un auditor que pregunte «¿cuál fue la evaluación de riesgo de este
     reporte?» tiene que encontrarla en el registro, no reconstruirla.

     El riesgo INICIAL es inmutable —es lo que se evaluó ese día, con lo que se
     sabía—. El RESIDUAL cambia al mitigar y va aparte, por eso no entra en la
     huella. */
  const riesgoInicial = (Number.isInteger(datos?.probabilidad) && typeof datos?.severidad === "string")
    ? { probabilidad: datos.probabilidad, severidad: datos.severidad.trim().toUpperCase() }
    : null;

  const registro = {
    id: id || `sgs_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`,
    tipo,
    creado,
    /* ═══ INMUTABLE: nada de esto se toca nunca más ═══ */
    descripcion,
    confidencial,
    /* En un confidencial la identidad va aparte, y el análisis se ve sin
       ella. Guardarla en el mismo campo haría que apareciera en cualquier
       listado. */
    reportante: confidencial ? null : reportante,
    identidadReservada: confidencial && reportante ? reportante : null,
    /* Las dimensiones del Sistema Integrado. Un evento puede tocar varias. */
    dimensiones: Array.isArray(datos?.dimensiones) ? datos.dimensiones : [],
    rolReportante: texto(datos?.rolReportante) || null,
    riesgoInicial,
    /* ═══ MUTABLE, pero solo agregando ═══ */
    estado: ESTADO_INICIAL,
    /* Mutable: cambia cuando se aplican medidas. Que baje es lo que el manual
       pide validar. */
    riesgoResidual: null,
    /* El análisis de barreras se completa durante el tratamiento. */
    barreras: [],
    anotaciones: [],
    bitacora: [{
      cuando: creado,
      quien: confidencial ? "(confidencial)" : reportante,
      accion: "creado",
      hacia: ESTADO_INICIAL,
    }],
  };

  registro.huella = huellaDe(registro);
  return { registro, errores: [] };
}

/* -------------------------------------------------------------- avanzar */

/**
 * Cambia el estado de un registro por el canal.
 *
 * Devuelve un registro NUEVO: no se modifica el original. Cada cambio queda en
 * la bitácora con quién, cuándo y por qué.
 */
export function cambiarEstado(registro, hacia, { quien, motivo, ahora = new Date() } = {}) {
  const errores = [];

  if (!registro?.estado) return { registro: null, errores: ["Registro inválido."] };
  if (!ESTADOS[hacia]) errores.push(`Estado desconocido: ${hacia}.`);
  else if (!transicionValida(registro.estado, hacia)) {
    /* Se dice a dónde SÍ se puede ir: un «transición inválida» a secas obliga
       a adivinar. */
    const posibles = ESTADOS[registro.estado].siguientes
      .map((s) => ESTADOS[s].etiqueta).join(", ");
    errores.push(posibles
      ? `Desde «${ESTADOS[registro.estado].etiqueta}» solo se puede pasar a: ${posibles}.`
      : `«${ESTADOS[registro.estado].etiqueta}» es un estado final: no se puede reabrir.`);
  }

  const q = texto(quien);
  if (!q) errores.push("Hace falta saber quién hace el cambio.");

  const m = texto(motivo);
  /* Cerrar sin motivo es lo que un auditor pregunta primero. */
  if (ESTADOS[hacia]?.final && m.length < 5) {
    errores.push("Cerrar un registro necesita un motivo.");
  }

  if (errores.length > 0) return { registro: null, errores };

  const cuando = (ahora instanceof Date ? ahora : new Date()).toISOString();

  return {
    registro: {
      ...registro,
      estado: hacia,
      bitacora: [...(registro.bitacora || []), {
        cuando, quien: q, accion: "cambio-estado",
        desde: registro.estado, hacia, motivo: m || null,
      }],
    },
    errores: [],
  };
}

/**
 * Agrega una anotación fechada.
 *
 * Es la ÚNICA forma de sumar información a un registro. El texto original no
 * se toca: si el análisis contradice lo que decía el reporte, las dos cosas
 * quedan, y esa diferencia es información.
 */
export function anotar(registro, textoNota, { quien, ahora = new Date() } = {}) {
  const errores = [];
  if (!registro?.id) return { registro: null, errores: ["Registro inválido."] };

  const t = texto(textoNota);
  if (t.length < 3) errores.push("La anotación está vacía.");

  const q = texto(quien);
  if (!q) errores.push("Hace falta saber quién anota.");

  if (errores.length > 0) return { registro: null, errores };

  const cuando = (ahora instanceof Date ? ahora : new Date()).toISOString();

  return {
    registro: {
      ...registro,
      anotaciones: [...(registro.anotaciones || []), { cuando, quien: q, texto: t }],
      bitacora: [...(registro.bitacora || []), { cuando, quien: q, accion: "anotado" }],
    },
    errores: [],
  };
}

/**
 * Registra el riesgo residual tras aplicar medidas.
 *
 * Es lo que el manual pide validar: que la mitigación haya bajado el riesgo.
 * No se puede registrar un residual si no hubo evaluación inicial — no habría
 * contra qué compararlo, y un residual suelto no dice nada.
 */
export function registrarResidual(registro, probabilidad, severidad, { quien, ahora = new Date() } = {}) {
  const errores = [];
  if (!registro?.id) return { registro: null, errores: ["Registro inválido."] };

  if (!registro.riesgoInicial) {
    errores.push("Este registro no tiene evaluación de riesgo inicial: "
      + "no hay contra qué comparar el residual.");
  }
  if (!Number.isInteger(probabilidad) || probabilidad < 1 || probabilidad > 5) {
    errores.push("La probabilidad tiene que ser un valor de 1 a 5.");
  }
  const sev = typeof severidad === "string" ? severidad.trim().toUpperCase() : "";
  if (!/^[A-E]$/.test(sev)) errores.push("La severidad tiene que ser una letra de A a E.");

  const q = texto(quien);
  if (!q) errores.push("Hace falta saber quién evalúa el riesgo residual.");

  if (errores.length > 0) return { registro: null, errores };

  const cuando = (ahora instanceof Date ? ahora : new Date()).toISOString();

  return {
    registro: {
      ...registro,
      riesgoResidual: { probabilidad, severidad: sev },
      bitacora: [...(registro.bitacora || []), {
        cuando, quien: q, accion: "riesgo-residual",
        motivo: `${probabilidad}${sev}`,
      }],
    },
    errores: [],
  };
}

/**
 * Registra el análisis de barreras.
 *
 * Es lo que hace al SGS más útil que un registro de hechos: obliga a decir qué
 * DEFENSA falló, no solo qué pasó. «Se cargó combustible contaminado» no es
 * accionable; «la barrera de muestreo no se aplicó» dice qué arreglar.
 *
 * No entra en la huella: el análisis se completa durante el tratamiento, y si
 * entrara, la huella se rompería al hacer el trabajo.
 */
export function registrarBarreras(registro, barreras, { quien, ahora = new Date() } = {}) {
  if (!registro?.id) return { registro: null, errores: ["Registro inválido."], avisos: [] };

  const { valido, errores, avisos, barreras: limpias } = validarBarreras(barreras);
  const q = texto(quien);
  if (!q) errores.push("Hace falta saber quién hizo el análisis de barreras.");
  if (!valido || errores.length > 0) return { registro: null, errores, avisos };

  const cuando = (ahora instanceof Date ? ahora : new Date()).toISOString();

  return {
    registro: {
      ...registro,
      barreras: limpias,
      bitacora: [...(registro.bitacora || []), {
        cuando, quien: q, accion: "analisis-barreras",
        motivo: orientacionDeBarreras(limpias),
      }],
    },
    errores: [],
    avisos,
  };
}

/**
 * Vista de un registro confidencial SIN la identidad.
 *
 * Es lo que se muestra en los listados y lo que va en el análisis. La
 * identidad queda accesible solo para quien administra el SGS y con un motivo
 * registrado — pero eso lo decide la pantalla, no este módulo.
 */
export function vistaAnonima(registro) {
  if (!registro) return null;
  const { identidadReservada, ...resto } = registro;
  void identidadReservada;
  return { ...resto, reportante: registro.confidencial ? "(confidencial)" : registro.reportante };
}

/* ------------------------------------------------------------ integridad */

/**
 * Verifica un conjunto de registros.
 *
 * Devuelve qué está bien, qué fue alterado y qué le falta. Es lo que se corre
 * antes de exportar y al importar: un archivo que dice «47 reportes» tiene que
 * poder demostrar que tiene 47 y que ninguno cambió.
 */
export function verificarConjunto(registros = []) {
  const lista = Array.isArray(registros) ? registros : [];

  const alterados = [];
  const sinHuella = [];
  const idsRepetidos = [];
  const vistos = new Set();

  for (const r of lista) {
    if (!r?.id) continue;
    if (vistos.has(r.id)) idsRepetidos.push(r.id);
    vistos.add(r.id);

    if (!r.huella) { sinHuella.push(r.id); continue; }
    if (!huellaIntacta(r)) alterados.push(r.id);
  }

  const intacto = alterados.length === 0 && sinHuella.length === 0 && idsRepetidos.length === 0;

  return {
    total: lista.length,
    intacto,
    alterados,
    sinHuella,
    idsRepetidos,
    resumen: intacto
      ? `${lista.length} registros, todos íntegros.`
      : `${lista.length} registros: ${alterados.length} alterados, `
        + `${sinHuella.length} sin huella, ${idsRepetidos.length} con id repetido.`,
  };
}

/* ------------------------------------------------------------ exportación */

/** Versión del formato de exportación. Sube si cambia la estructura. */
export const VERSION_EXPORTACION = 1;

/**
 * Arma el paquete auditable.
 *
 * Lleva la verificación adentro: un auditor tiene que poder comprobar el
 * archivo sin la aplicación. Y lleva la versión del programa que lo produjo,
 * como el resto de HeliCalc.
 *
 * Los confidenciales van SIN identidad salvo que se pida explícitamente, y
 * pedirla queda anotado en el propio paquete.
 */
export function exportar(registros = [], opciones = {}) {
  const {
    ahora = new Date(),
    operador = null,
    version = null,
    conIdentidades = false,
    quienExporta = null,
    motivoIdentidades = null,
  } = opciones;

  const lista = Array.isArray(registros) ? registros : [];
  const verificacion = verificarConjunto(lista);

  const salida = lista.map((r) => (conIdentidades ? r : vistaAnonima(r)));

  return {
    formato: "helicalc-sgs",
    version: VERSION_EXPORTACION,
    generado: (ahora instanceof Date ? ahora : new Date()).toISOString(),
    operador,
    aplicacion: version,
    quienExporta: texto(quienExporta) || null,
    /* Que el paquete diga si trae identidades es parte de la evidencia: un
       auditor tiene que saber qué está mirando. */
    incluyeIdentidades: conIdentidades === true,
    motivoIdentidades: conIdentidades ? (texto(motivoIdentidades) || null) : null,
    verificacion,
    registros: salida,
  };
}

/**
 * Lee un paquete exportado.
 *
 * Comprueba el formato y la integridad ANTES de devolver nada: importar un
 * archivo alterado sin avisar convertiría el problema en permanente.
 */
export function importar(paquete) {
  if (!paquete || typeof paquete !== "object") {
    return { registros: [], errores: ["El archivo no tiene el formato esperado."] };
  }
  if (paquete.formato !== "helicalc-sgs") {
    return { registros: [], errores: ["Este archivo no es una exportación de SGS de HeliCalc."] };
  }
  if (!Number.isInteger(paquete.version) || paquete.version > VERSION_EXPORTACION) {
    return {
      registros: [],
      errores: [`El archivo es de una versión más nueva (${paquete.version}). Actualiza la aplicación.`],
    };
  }

  const lista = Array.isArray(paquete.registros) ? paquete.registros : [];
  const verificacion = verificarConjunto(lista);

  const avisos = [];
  if (!verificacion.intacto) {
    /* NO se rechaza el archivo: puede ser el único respaldo que queda. Se
       importa y se marca, que es lo honesto — pero se dice fuerte. */
    avisos.push(`ATENCIÓN: ${verificacion.resumen} Los registros marcados no se `
      + "pueden presentar como evidencia sin explicar qué pasó.");
  }
  if (paquete.incluyeIdentidades) {
    avisos.push("Este archivo incluye identidades de reportes confidenciales.");
  }

  return { registros: lista, verificacion, avisos, errores: [] };
}

/* ------------------------------------------------------------ indicadores */

/** Umbrales de volumen. Medidos: 2000 registros pesan unos 700 KB. */
export const VOLUMEN = { avisar: 500, urgente: 1500 };

/**
 * Salud del volumen del registro.
 *
 * Cada guardado reescribe el conjunto ENTERO y la confirmación lo relee. Con
 * miles de registros eso se vuelve lento y consume espacio, y el día que la
 * cuota se agote los reportes empiezan a fallar.
 *
 * No se archiva solo: eso sería borrar sin que nadie lo pida. Se avisa a
 * tiempo para que el operador exporte y archive según su manual.
 */
export function saludDelVolumen(registros = [], { avisar = VOLUMEN.avisar, urgente = VOLUMEN.urgente } = {}) {
  const lista = Array.isArray(registros) ? registros : [];
  const total = lista.length;

  /* Se mide el tamaño real, no una estimación por registro: un reporte con
     veinte anotaciones pesa mucho más que uno recién creado. */
  let kb = null;
  try {
    kb = Math.round(JSON.stringify(lista).length / 1024);
  } catch { /* referencias circulares: no debería pasar, pero no se rompe */ }

  const cerrados = lista.filter((r) => estaCerrado(r?.estado)).length;

  if (total >= urgente) {
    return {
      total, kb, cerrados, nivel: "urgente",
      mensaje: `${total} registros (${kb} KB). Cada nuevo reporte reescribe todo el `
        + `conjunto: exporta y archiva los cerrados antes de que empiece a fallar.`,
    };
  }
  if (total >= avisar) {
    return {
      total, kb, cerrados, nivel: "aviso",
      mensaje: `${total} registros (${kb} KB), ${cerrados} cerrados. `
        + `Conviene exportar y archivar según la cadencia del manual.`,
    };
  }
  return { total, kb, cerrados, nivel: "bien", mensaje: null };
}

/**
 * Indicadores con denominador y tendencia.
 *
 * «12 eventos» no distingue una operación de 100 horas de una de 3.000. Sin
 * denominador y sin período, un indicador de seguridad no dice nada — y BARS
 * pide meta y tendencia, no conteos.
 */
export function indicadores(registros = [], { horasVoladas = null, desde = null, hasta = null } = {}) {
  const lista = Array.isArray(registros) ? registros : [];

  const dentro = (r) => {
    if (!r?.creado) return false;
    if (desde && r.creado < desde) return false;
    if (hasta && r.creado > hasta) return false;
    return true;
  };

  const periodo = lista.filter(dentro);
  const cerrados = periodo.filter((r) => estaCerrado(r.estado));
  const eficaces = periodo.filter((r) => r.estado === "cerrado-eficaz");
  const abiertos = periodo.filter((r) => !estaCerrado(r.estado));

  const horas = num(horasVoladas);

  return {
    total: periodo.length,
    abiertos: abiertos.length,
    cerrados: cerrados.length,
    eficaces: eficaces.length,
    /* Sin horas voladas la tasa es null, NO cero: cero significaría «ningún
       evento por mil horas», que es una afirmación, y no se sabe. */
    tasaPorMilHoras: horas && horas > 0
      ? Math.round((periodo.length / horas) * 1000 * 10) / 10
      : null,
    /* Cuánto tarda en cerrarse un registro. Es el indicador que muestra si el
       sistema procesa o solo acumula. */
    diasPromedioCierre: promedioDias(cerrados),
    proporcionEficaz: cerrados.length > 0
      ? Math.round((eficaces.length / cerrados.length) * 100)
      : null,
  };
}

function promedioDias(cerrados) {
  const dias = [];
  for (const r of cerrados) {
    const cierre = (r.bitacora || []).filter((b) => ESTADOS[b.hacia]?.final).pop();
    if (!cierre?.cuando || !r.creado) continue;
    const d = (new Date(cierre.cuando) - new Date(r.creado)) / 86400000;
    if (Number.isFinite(d) && d >= 0) dias.push(d);
  }
  if (dias.length === 0) return null;
  return Math.round((dias.reduce((a, b) => a + b, 0) / dias.length) * 10) / 10;
}

/**
 * Compara dos períodos.
 *
 * Un indicador sin comparación no dice si algo mejoró. Devuelve `null` en la
 * variación cuando falta uno de los dos: inventar un 0 % diría que no cambió
 * nada, y no se sabe.
 */
export function tendencia(actual, anterior) {
  const cambio = (a, b) => {
    if (!Number.isFinite(a) || !Number.isFinite(b) || b === 0) return null;
    return Math.round(((a - b) / b) * 100);
  };

  return {
    total: cambio(actual?.total, anterior?.total),
    tasaPorMilHoras: cambio(actual?.tasaPorMilHoras, anterior?.tasaPorMilHoras),
    diasPromedioCierre: cambio(actual?.diasPromedioCierre, anterior?.diasPromedioCierre),
  };
}
